import { useMemo } from "react";

import { any } from "psims/lib/collections";
import { is } from "psims/lib/type-assertions";
import { recordActionAsEnum } from "psims/models/api/data-submission-record-action";
import NotificationMessage, { NotificationPart } from "../../shared/notification-message";
import { UseSubmit } from "../../shared/use-submit";
import { UseMsoComment } from "../shared/mso-comment/use-mso-comment";
import { UseImporterAnnualActivities } from "./annual-activities/use-importer-annual-activities";
import { UseImporterImports } from "../shared/imports/use-importer-imports";
import { UseImporterAnnualActivityNavigation } from "./use-importer-annual-activity-navigation";
import { UseImporterAnnualActivityProgress } from "./use-importer-annual-activity-progress";
import { UseMsoStockholdingStorages } from "../shared/mso-stockholding-storages/use-mso-stockholding-storages";
import useEnforceableArrangements from "../shared/enforceable-arrangements/use-enforceable-arrangements";
import useAustralianControllingCorporations from "../shared/australian-controlling-corporations/use-australian-controlling-corporations";

interface UseImporterAnnualActivityNotificationsProps {
  annualActivitiesCtrl: UseImporterAnnualActivities;
  commentsCtrl: UseMsoComment;
  importsCtrl: UseImporterImports;
  navCtrl: UseImporterAnnualActivityNavigation;
  progressCtrl: UseImporterAnnualActivityProgress;
  serviceMessages: Array<string>;
  msoStockholdingStorageCtrl: UseMsoStockholdingStorages;
  enforceableArrangementsCtrl: ReturnType<typeof useEnforceableArrangements>;
  submitCtrl: UseSubmit;
  australianControllingCorporationsCtrl: ReturnType<typeof useAustralianControllingCorporations>;
}

function useImporterAnnualActivityNotifications({
  annualActivitiesCtrl,
  commentsCtrl,
  importsCtrl,
  navCtrl,
  progressCtrl,
  serviceMessages,
  msoStockholdingStorageCtrl,
  enforceableArrangementsCtrl,
  australianControllingCorporationsCtrl,
  submitCtrl
}: UseImporterAnnualActivityNotificationsProps) {

 const validationNotifications = useMemo(() => {
    const currentIndex = progressCtrl.currentStep.index;

    // ── Step 0: Imports + Annual Activities ──────────────────────────────
    if (currentIndex === 0) {
      return [
        // Imports
        ...(importsCtrl.updateImports
          .filter(a => a.data.recordAction !== recordActionAsEnum('Delete'))
          .map(s =>
            Object.entries(s.validations)
              .filter(([k, v]) => v.notification.parts != null)
              .filter(([k, v]) => v.validationResult.code !== 'invalid_required' || navCtrl.lastSaveAttemptIndex != null)
              .map(([k, v]) => v.notification)
              .filter(is)
          )
          .flat() as Array<{ parts: Array<NotificationPart> }>)
          .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),

        // Annual activities
        ...(annualActivitiesCtrl.updateAnnualActivities
          .filter(a => a.data.recordAction !== recordActionAsEnum('Delete'))
          .map(s =>
            Object.entries(s.validations)
              .filter(([k, v]) => v.notification.parts != null)
              .filter(([k, v]) => v.validationResult.code !== 'invalid_required' || navCtrl.lastSaveAttemptIndex != null)
              .map(([k, v]) => v.notification)
              .filter(is)
          )
          .flat() as Array<{ parts: Array<NotificationPart> }>)
          .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),

        // Comments
        ...(commentsCtrl.data.recordAction === recordActionAsEnum('Delete') ? [] : [
          ...commentsCtrl.validations
            .filter(v => v.notification.parts != null)
            .map(v => v.notification.parts)
            .filter(is)
            .map(parts => ({ content: NotificationMessage({ parts }) }))
        ]),
      ];
    }

    // ── Step 1: Storage ──────────────────────────────────────────────────
    if (currentIndex === 1) {
      return [
        // Storage inactive-product delete errors
        ...(msoStockholdingStorageCtrl.updateStorages
          .filter(s => s.data.recordAction !== recordActionAsEnum('Delete'))
          .map(s => {
            const d = (s.validations as Record<string, any>)?.delete;
            if (d == null) return [];
            return [d.notification].filter(is);
          })
          .flat() as Array<{ parts: Array<NotificationPart> }>)
          .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),

        // Storage field-level validations
        ...(msoStockholdingStorageCtrl.updateStorages
          .filter(s => s.data.recordAction !== recordActionAsEnum('Delete'))
          .map(s =>
            Object.entries(s.validations as Record<string, any>)
              .filter(([k, v]) => v?.notification?.parts != null)
              .filter(([k, v]) => v.validationResult?.code !== 'invalid_required' || navCtrl.lastSaveAttemptIndex != null)
              .map(([k, v]) => v.notification)
              .filter(is)
          )
          .flat() as Array<{ parts: Array<NotificationPart> }>)
          .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),
      ];
    }

    // ── Step 2: Enforceable Arrangements ────────────────────────────────
    if (currentIndex === 2) {
      return [
        ...(enforceableArrangementsCtrl.rows
          .map(s =>
            Object.entries(s.validations as Record<string, any>)
              .filter(([k, v]) => v?.notification?.parts != null)
              .filter(([k, v]) => v.validationResult?.code !== 'invalid_required' || navCtrl.lastSaveAttemptIndex != null)
              .map(([k, v]) => v.notification)
              .filter(is)
          )
          .flat() as Array<{ parts: Array<NotificationPart> }>)
          .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),
      ];
    }

      // ── Step 3: Australian Controlling Corporations ──────────────────────
    if (currentIndex === 3) {
      return [
        ...(australianControllingCorporationsCtrl.rows
          .map(s =>
            Object.entries(s.validations as Record<string, any>)
              .filter(([k, v]) => v?.notification?.parts != null)
              .filter(([k, v]) => v.validationResult?.code !== 'invalid_required' || navCtrl.lastSaveAttemptIndex != null)
              .map(([k, v]) => v.notification)
              .filter(is)
          )
          .flat() as Array<{ parts: Array<NotificationPart> }>)
          .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),
      ];
    }


    // ── Submit page (index 4) ────────────────────────────────────────────
    return [
      ...submitCtrl.notifications,

      // Annual activities — inactive product delete errors
      ...(annualActivitiesCtrl.updateAnnualActivities
        .filter(s => s.data.recordAction !== recordActionAsEnum('Delete'))
        .map(s => {
          const { delete: d } = s.validations;
          if (d == null) return [];
          return [d.notification].filter(is);
        })
        .flat() as Array<{ parts: Array<NotificationPart> }>)
        .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),

      // Storage — inactive product delete errors
      ...(msoStockholdingStorageCtrl.updateStorages
        .filter(s => s.data.recordAction !== recordActionAsEnum('Delete'))
        .map(s => {
          const d = (s.validations as Record<string, any>)?.delete;
          if (d == null) return [];
          return [d.notification].filter(is);
        })
        .flat() as Array<{ parts: Array<NotificationPart> }>)
        .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),

      // Imports — inactive product delete errors
      ...(importsCtrl.updateImports
        .filter(s => s.data.recordAction !== recordActionAsEnum('Delete'))
        .map(s => {
          const { delete: d } = s.validations;
          if (d == null) return [];
          return [d.notification].filter(is);
        })
        .flat() as Array<{ parts: Array<NotificationPart> }>)
        .map(notification => ({ content: NotificationMessage({ parts: notification.parts }) })),
    ];

  }, [
    annualActivitiesCtrl.updateAnnualActivities,
    commentsCtrl.data,
    commentsCtrl.validations,
    importsCtrl.updateImports,
    navCtrl.lastSaveAttemptIndex,
    progressCtrl.currentStep.index,
    msoStockholdingStorageCtrl.updateStorages,
    enforceableArrangementsCtrl.rows,
    australianControllingCorporationsCtrl.rows,
    submitCtrl,
  ]);

  const infoNotifications = useMemo(() => {
    const messages: Array<{ content: JSX.Element }> = [];

    if (
      progressCtrl.currentStep.index === 0 &&
      any(
        annualActivitiesCtrl.updateAnnualActivities
          .map(a => a.data)
          .filter(a => a.recordAction !== recordActionAsEnum('Delete')),
        a => a.enforceableArrangements === true
      )
    ) {
      messages.push({
        content: NotificationMessage({
          parts: [
            <span key={1}>
              You have selected 'Enforceable arrangements'. Please provide a copy of the legally
              enforceable arrangement/s to the department by uploading it to the Documents page
              under the Manage company menu.
            </span>
          ]
        })
      });
    }

    return [
      ...(progressCtrl.currentStep.index === 0
        ? annualActivitiesCtrl.updateAnnualActivities.map(a => [
            ...(a.validationAlerts.totalImportVolumeOnBehalf?.notification.parts == null ? [] : [{ content: NotificationMessage({ parts: a.validationAlerts.totalImportVolumeOnBehalf.notification.parts }) }]),
            ...(a.validationAlerts.totalImportVolumeOthers?.notification.parts == null    ? [] : [{ content: NotificationMessage({ parts: a.validationAlerts.totalImportVolumeOthers.notification.parts }) }]),
            ...(a.validationAlerts.totalAnnualVolume?.notification.parts == null          ? [] : [{ content: NotificationMessage({ parts: a.validationAlerts.totalAnnualVolume.notification.parts }) }]),
          ])
        : []
      ),
      ...messages,
    ].flat();
  }, [annualActivitiesCtrl.updateAnnualActivities, progressCtrl.currentStep.index]);

  const serviceNotifications = useMemo(() => {
    return serviceMessages.map(message => ({
      content: NotificationMessage({ parts: [message] })
    }));
  }, [serviceMessages]);

  return {
    infoNotifications,
    serviceNotifications,
    validationNotifications,
  };
}

export default useImporterAnnualActivityNotifications;

export type UseImporterAnnualActivityNotifications = ReturnType<typeof useImporterAnnualActivityNotifications>;